import { useMemo, useState, useEffect } from 'react';

import { collection, getDocs, orderBy, Query, QuerySnapshot, query, where } from 'firebase/firestore';

import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import Membership, { MembershipStatus, MEMBERSHIP_STATUSES } from '../types/Membership';
import PersonIdentity from '../types/PersonIdentity';

interface UseAllMembersProps {
  includePendingMemberships?: boolean;
}

/**
 * Fetches all unique persons across the current user's active orgs.
 * Queries memberships per-org and dedupes by person ID, using the name/email/picture
 * already denormalized onto the membership doc (same data source OrgMemberDirectory uses)
 * instead of a second profile-by-ID lookup — that lookup hit the profile collection's
 * per-document areInASharedOrg security rule via a batched `in` query, and Firestore denies
 * the *entire* batch if any one document in it fails the rule, silently dropping everyone
 * else in that batch of IDs.
 */
export default function useAllMembers({ includePendingMemberships = false }: UseAllMembersProps = {}) {
  const { getAllActiveOrgs, userState } = useData();

  const orgIds = useMemo(() => getAllActiveOrgs(), [getAllActiveOrgs]);

  const [persons, setPersons] = useState<PersonIdentity[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Stable key for orgIds to avoid unnecessary re-fetches
  const orgIdsKey = useMemo(() => [...orgIds].sort().join(','), [orgIds]);

  useEffect(() => {
    if (userState.status !== 'ready') {
      return;
    }

    if (orgIds.length === 0) {
      setPersons([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchMembers() {
      setIsLoading(true);

      try {
        const statuses: MembershipStatus[] = [MEMBERSHIP_STATUSES.ACTIVE, MEMBERSHIP_STATUSES.DEPRECATED_ACTIVE];
        if (includePendingMemberships) {
          statuses.push(MEMBERSHIP_STATUSES.PENDING);
        }

        // Query each org's membership subcollection directly (path already scopes by orgId)
        // Batch in groups of 5 to avoid overwhelming Firestore
        const BATCH_SIZE = 5;
        const results: QuerySnapshot<Membership>[] = [];
        for (let i = 0; i < orgIds.length; i += BATCH_SIZE) {
          const batch = orgIds.slice(i, i + BATCH_SIZE);
          const batchResults = await Promise.all(
            batch.map(orgId =>
              getDocs(
                query(
                  collection(firestore, `org/${orgId}/membership`) as Query<Membership>,
                  where('status', 'in', statuses),
                  orderBy('name')
                )
              )
            )
          );
          if (cancelled) return;
          results.push(...batchResults);
        }

        if (cancelled) return;

        // Dedupe by person ID, keeping the denormalized name/email/picture already on the membership doc
        const byPersonId = new Map<string, PersonIdentity>();
        results.forEach(snapshot => {
          snapshot.docs.forEach(doc => {
            const membership = doc.data();
            if (!byPersonId.has(membership.itemId)) {
              byPersonId.set(membership.itemId, {
                itemId: membership.itemId,
                name: membership.name,
                email: membership.email,
                picture: membership.picture,
                ghin: membership.ghin,
                ghinIndex: membership.ghinIndex,
                isPro: membership.isPro,
              });
            }
          });
        });

        setPersons(Array.from(byPersonId.values()));
        setIsLoading(false);
      } catch (fetchError) {
        console.error('Error fetching all members:', fetchError);
        if (!cancelled) {
          setPersons([]);
          setError(fetchError as Error);
          setIsLoading(false);
        }
      }
    }

    fetchMembers();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgIdsKey, userState.status, includePendingMemberships]);

  // Sort by name for display
  const sortedPersons = useMemo(() => {
    if (!persons) return null;
    return [...persons].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [persons]);

  return {
    persons: sortedPersons,
    isLoading,
    error,
  };
}
